import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { BaseQueryArgs, BaseQueryRequestFn, QueryArgs, QueryError, QueryExtraOptions, QueryResult } from "./Query";
import { apiPath, xsrfCookieName, xsrfHeaderName } from "../const";

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.xsrfCookieName = xsrfCookieName;
axios.defaults.xsrfHeaderName = xsrfHeaderName;

type AxiosBaseQueryArgs = BaseQueryArgs

type AxiosQueryArgs<D = unknown> = QueryArgs | AxiosRequestConfig<D>

type AxiosQueryExtraOptions = QueryExtraOptions

type AxiosQueryResult<T = unknown, D = unknown> = QueryResult<T, AxiosResponse<T, D>>

type AxiosQueryError<T = unknown, D = unknown> = QueryError<T, AxiosError<T, D>>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isAxiosQueryError = (error: unknown): error is AxiosQueryError => {
    return (error as AxiosQueryError)?.status !== undefined
        && (error as AxiosQueryError)?.statusText !== undefined
        && (error as AxiosQueryError)?.meta?.result?.isAxiosError;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isAxiosQueryResult = (result: unknown): result is AxiosQueryResult => {
    return (result as AxiosQueryResult)?.data !== undefined
        && (result as AxiosQueryResult)?.meta?.result?.data != undefined;
};

const baseQueryRequest =
    (
        { baseUrl, ...baseOptions }: AxiosBaseQueryArgs = { baseUrl: "" }
    ): BaseQueryRequestFn<AxiosQueryArgs, AxiosQueryResult, AxiosQueryError, AxiosQueryExtraOptions> =>
    async (
        args: AxiosQueryArgs,
        extraOptions?: QueryExtraOptions,
    ): Promise<AxiosQueryResult | AxiosQueryError> => {

        const {
            baseURL: overriddenBaseUrl = undefined,
            url,
            method = "GET",
            headers = {},
            params = undefined,
            data = undefined,
            ...rest
        } = typeof args === "string" ? { url: args } : args;

        const config = {
            ...baseOptions,
            url: (overriddenBaseUrl ? overriddenBaseUrl : baseUrl) + url,
            method,
            headers,
            params,
            data,
            ...extraOptions,
            ...rest,
        } as AxiosRequestConfig;

        try {

            const result = await axios(config);

            console.debug("axiosBaseQuery: result", result);

            if (result.request.responseURL.endsWith("/login")) {
                window.location = result.request.responseURL;
            }

            const meta = { result: result };

            return {
                data: result.data,
                meta,
            } as AxiosQueryResult;

        } catch (axiosError) {

            const err = axiosError as AxiosError;

            console.debug("axiosBaseQuery: error", err);

            if ([ 401, 403 ].includes(err.response?.status ?? 0)) {
                window.location = err.request?.requestURL ?? "/login";
            }

            const meta = { result: err };

            return {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                meta,
            } as AxiosQueryError;

        }
    };

const queryRequest = baseQueryRequest({ baseUrl: apiPath });

export {
    queryRequest,
};